import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { Box } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// Local imports
import { App } from 'BMapsSrc/BMapsApp';
import { Compound } from 'BMapsModel';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { createJob } from 'BMapsRedux/jobs/thunks';
import { useDispatch } from 'BMapsRedux';
import { JobTypes, lookupJobDefinition } from 'BMapsSrc/jobman';
import { UserActions } from 'BMapsSrc/cmds';
import { TextArea } from 'BMapsUI/common/TextFields';
import Select from 'BMapsUI/common/Select';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import LinkLikeButton from 'BMapsUI/common/LinkLikeButton';
import { RXN_ENUM_REACTIONS } from 'BMapsSrc/jobman/jobs/rxnEnumReactions';
import { MoleculeSketcher } from 'BMapsSrc/MoleculeSketcher';
import { ReactionSketcher } from 'BMapsSrc/ReactionSketcher';
import ReactionSchemePreview from './ReactionSchemePreview';
import {
    ImportErrors, PlaceHolder,
} from '../../import_components';
import { BasicTextField } from '../../../SimulationPane/Components';

// Molexar generation tasks. de_novo needs no input SMILES; the others require
// a --start_smiles scaffold/fragment.
const MOLEXAR_TASKS = [
    { value: 'de_novo', label: 'De novo (no input SMILES)' },
    {
        value: 'scaffold_decoration',
        label: 'Scaffold decoration',
        hint: 'One [*] marks where new R-groups are added.',
        example: 'CC1=CN(C2=CC(CN(C3=NC=C(CC4=CC=C(N5C=CC=N5)C=C4[*])S3)CC6)=C6C=C2)C(C)=N1',
    },
    {
        value: 'motif_extension',
        label: 'Motif extension',
        hint: 'One [*] marks where the motif is grown.',
        example: '[*]N1C=CC=N1',
    },
    {
        value: 'linker_design',
        label: 'Linker design',
        hint: 'Two fragments (period-separated); each [*] is a point to be linked.',
        example: 'CC1=CN([*])C(C)=N1.[*]N1C=CC=N1',
    },
    {
        value: 'scaffold_morphing',
        label: 'Scaffold morphing',
        hint: 'Two [*]-tagged fragments; the scaffold connecting them is redesigned.',
        example: 'CC1=CN(C2=CC(CN([*])CC3)=C3C=C2)C(C)=N1.[*]CC1=CC=C(N2C=CC=N2)C=C1',
    },
    {
        value: 'superstructure',
        label: 'Superstructure',
        hint: 'No [*]; every atom of the input is retained in the output.',
        example: 'CC1=CN(C2=CC(CN(C3=NC=C(CC4=CC=C(N5C=CC=N5)C=C4)S3)CC6)=C6C=C2)C(C)=N1',
    },
];

// Optional property-conditioning knobs (run_inference.py float flags). Blank =
// unconditioned. mol_wt / mol_logp are surfaced by default; the rest live under
// "advanced" to keep the panel tidy.
const MOLEXAR_PROPERTIES = [
    {
        key: 'mol_wt', label: 'Molecular weight (blank = off)', primary: true, title: 'Target molecular weight in Daltons',
    },
    {
        key: 'mol_logp', label: 'LogP (blank = off)', primary: true, title: 'Target calculated logP',
    },
    { key: 'mol_hac', label: 'Heavy atom count (blank = off)', title: 'Target number of heavy atoms' },
    { key: 'mol_hbdc', label: 'H-bond donors (blank = off)', title: 'Target hydrogen-bond donor count' },
    { key: 'mol_hbac', label: 'H-bond acceptors (blank = off)', title: 'Target hydrogen-bond acceptor count' },
    { key: 'mol_rotbc', label: 'Rotatable bonds (blank = off)', title: 'Target rotatable-bond count' },
    { key: 'mol_tpsa', label: 'TPSA (blank = off)', title: 'Target topological polar surface area' },
    { key: 'mol_qed', label: 'QED (blank = off)', title: 'Target QED drug-likeness score (0-1)' },
    { key: 'mol_sas', label: 'SA score (blank = off)', title: 'Target synthetic accessibility score' },
];
const MOLEXAR_PROPERTY_KEYS = MOLEXAR_PROPERTIES.map((p) => p.key);

export default function CompoundGenerationTab({ initData }) {
    const dispatch = useDispatch();
    const [moleculeGenerationPrograms] = useState(
        [JobTypes.REINVENT, JobTypes.DRUGFLOW, JobTypes.RXN_ENUM, JobTypes.MOLEXAR]
    );
    const [chosenProgram, setChosenProgram] = useState(moleculeGenerationPrograms[0]);
    const [compounds, setCompounds] = useState(App.Workspace.getLoadedCompounds());
    const [errors, setErrors] = useState([]);
    const [molDataText, setMolDataText] = useState(
        initData?.selectedCompounds?.map((x) => x.getSmiles()).join('\n')
        || initData?.molDataText
        || ''
    );
    const [jobName, setJobName] = useState(initData?.jobName || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    // DrugFlow (structure-based) state
    const [refLigandSpec, setRefLigandSpec] = useState('');
    const [nSamples, setNSamples] = useState(10);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [advanced, setAdvanced] = useState({});
    // Rxn-Enum reaction picker state
    const [reactionNames, setReactionNames] = useState([]);
    const [customRxnfiles, setCustomRxnfiles] = useState([]);
    const [showDrawReaction, setShowDrawReaction] = useState(false);
    // Molexar (pocket-conditioned) state
    const [mxRefSpec, setMxRefSpec] = useState('');
    const [mxNumSamples, setMxNumSamples] = useState(10);
    const [mxTask, setMxTask] = useState('de_novo');
    const [mxStartSmiles, setMxStartSmiles] = useState('');
    const [mxStartMolfile, setMxStartMolfile] = useState('');
    const [mxAdvanced, setMxAdvanced] = useState({});
    const [mxShowAdvanced, setMxShowAdvanced] = useState(false);

    const isDrugFlow = chosenProgram === JobTypes.DRUGFLOW;
    const isMolexar = chosenProgram === JobTypes.MOLEXAR;
    const loadedCompounds = App.Workspace.getLoadedCompounds();

    function finishSubmit(results, error) {
        if (results) {
            setMolDataText('');
            setJobName('');
            setRefLigandSpec('');
            setAdvanced({});
            setShowAdvanced(false);
            setReactionNames([]);
            setCustomRxnfiles([]);
            setMxRefSpec('');
            setMxStartSmiles('');
            setMxStartMolfile('');
            setMxTask('de_novo');
            setMxAdvanced({});
            setMxShowAdvanced(false);
            setChosenProgram(moleculeGenerationPrograms[0]);
            setErrors([]);
            UserActions.OpenImport('jobs_compounds_tab', { jobid: results?.jobid });
            return true;
        }
        setErrors([`The job submission failed: ${error || 'Unspecified error'}`]);
        return false;
    }

    async function generateCompounds(compoundSmiles) {
        const smiles = Array.isArray(compoundSmiles) ? compoundSmiles : [compoundSmiles];
        const validationErrs = validateInput(smiles, chosenProgram);
        if (validationErrs.length > 0) {
            setErrors(validationErrs);
            return false;
        }

        setIsSubmitting(true);
        const jobParams = chosenProgram === JobTypes.RXN_ENUM
            ? {
                smiles,
                jobName,
                // exact reaction picked from the dropdown; empty = all reactions
                reaction_names: reactionNames.length ? reactionNames : undefined,
                // reactions drawn in the 2D editor (RXN molfiles); run in union with
                // any named picks, or alone if no names are chosen.
                custom_rxnfiles: customRxnfiles.length ? customRxnfiles : undefined,
                input_role: 'reactant',
                max_products_per_combo: 50,
                max_unique: 0,
            }
            : { smiles, jobName };
        const { results, error } = await dispatch(
            createJob(chosenProgram, jobParams)
        );
        setIsSubmitting(false);
        return finishSubmit(results, error);
    }

    async function generateWithDrugFlow() {
        const refLigand = loadedCompounds.find((c) => c.resSpec === refLigandSpec);
        if (!refLigand) {
            setErrors(['Select a reference ligand to define the binding pocket.']);
            return false;
        }

        setIsSubmitting(true);
        const { pdb, error: pdbError } = await UserActions.GetProteinPdb(refLigand);
        if (pdbError || !pdb) {
            setIsSubmitting(false);
            setErrors([`Could not get protein structure: ${pdbError || 'no PDB produced'}`]);
            return false;
        }

        const requestData = {
            protein_pdb: pdb,
            ref_ligand_sdf: Compound.makeSDF([refLigand], {}),
            n_samples: Number(nSamples) || 10,
            jobName,
        };
        // Optional advanced params: numeric ones cast to Number, molecule_size
        // stays a string (it may be a single int or a "min,max" range).
        const numericAdvanced = ['batch_size', 'n_steps', 'pocket_distance_cutoff', 'seed'];
        for (const key of numericAdvanced) {
            const val = advanced[key];
            if (val !== undefined && val !== '') {
                requestData[key] = Number(val);
            }
        }
        if (advanced.molecule_size) {
            requestData.molecule_size = advanced.molecule_size;
        }
        const { results, error } = await dispatch(
            createJob(JobTypes.DRUGFLOW, requestData)
        );
        setIsSubmitting(false);
        return finishSubmit(results, error);
    }

    // Resolve a reference ligand + its protein PDB for Molexar's pocket.
    async function resolveStructureInputs(spec) {
        const refLigand = loadedCompounds.find((c) => c.resSpec === spec);
        if (!refLigand) {
            setErrors(['Select a reference ligand to define the binding pocket.']);
            return null;
        }
        const { pdb, error: pdbError } = await UserActions.GetProteinPdb(refLigand);
        if (pdbError || !pdb) {
            setErrors([`Could not get protein structure: ${pdbError || 'no PDB produced'}`]);
            return null;
        }
        return { refLigand, pdb };
    }

    async function generateWithMolexar() {
        if (mxTask !== 'de_novo' && !mxStartSmiles.trim() && !mxStartMolfile) {
            setErrors([`The "${mxTask}" task requires an input structure (draw it or type SMILES).`]);
            return false;
        }

        setIsSubmitting(true);
        const resolved = await resolveStructureInputs(mxRefSpec);
        if (!resolved) {
            setIsSubmitting(false);
            return false;
        }
        const { refLigand, pdb } = resolved;

        const requestData = {
            protein_pdb: pdb,
            ref_ligand_sdf: Compound.makeSDF([refLigand], {}),
            num_samples: Number(mxNumSamples) || 10,
            generation_task: mxTask,
            jobName,
        };
        if (mxTask !== 'de_novo') {
            // A drawn structure (start_molfile) takes precedence; the backend
            // converts it to SMILES with RDKit. Otherwise use typed SMILES.
            if (mxStartMolfile) {
                requestData.start_molfile = mxStartMolfile;
            } else if (mxStartSmiles.trim()) {
                requestData.start_smiles = mxStartSmiles.trim();
            }
        }
        const numericAdvanced = [
            'pocket_radius', 'max_atoms', 'temperature', 'top_p', 'top_k', 'batch_size',
            ...MOLEXAR_PROPERTY_KEYS,
        ];
        for (const key of numericAdvanced) {
            const val = mxAdvanced[key];
            if (val !== undefined && val !== '') {
                requestData[key] = Number(val);
            }
        }
        const { results, error } = await dispatch(
            createJob(JobTypes.MOLEXAR, requestData)
        );
        setIsSubmitting(false);
        return finishSubmit(results, error);
    }

    function handleSubmitClick() {
        if (isDrugFlow) {
            generateWithDrugFlow();
            return;
        }
        if (isMolexar) {
            generateWithMolexar();
            return;
        }
        const smiles = molDataText
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s !== '');
        generateCompounds(smiles);
    }

    function validateInput(input, program) {
        if (program === JobTypes.REINVENT) {
            // Allowed bracketed tokens for REINVENT mol2mol_scaffold_generic model
            const allowedTokens = new Set([
                '[11CH3]',
                '[11C]',
                '[11c]',
                '[123I]',
                '[18F]',
                '[19F]',
                '[76Br]',
                '[C@@H]',
                '[C@@]',
                '[C@H]',
                '[C@]',
                '[N+]',
                '[N@+]',
                '[N@@+]',
                '[O-]',
                '[O]',
                '[S@@]',
                '[S@]',
                '[n+]',
                '[n-]',
                '[nH]',
            ]);

            const unsupported = input.flatMap((s) => {
                // Extract SMILES part (before any whitespace/name)
                const smilesOnly = s.split(/\s/)[0];

                // Find all bracketed tokens
                const bracketedTokens = smilesOnly.match(/\[[^\]]+\]/g) || [];

                // Find tokens that are not in the allowed list
                const badTokens = bracketedTokens.filter((token) => !allowedTokens.has(token));

                return badTokens.length > 0 ? [{ smiles: s, groups: badTokens }] : [];
            });

            return unsupported.map(({ smiles, groups }) => `Unsupported tokens (${groups.join(', ')}) in SMILES: ${smiles}`);
        }
        return [];
    }

    const handleCompoundSelect = (compound) => {
        const newInput = compound.getSmiles();
        if (!molDataText || molDataText === '') {
            setMolDataText(newInput);
            return;
        }
        setMolDataText(`${molDataText}\n${newInput}`);
    };

    // Update the Submit button (disabled / tooltip) as necessary
    let submitBtnTooltip = 'Click to submit compound generation job';
    let disabled = false;

    if (isSubmitting) {
        submitBtnTooltip = 'Submitting job...';
        disabled = true;
    } else if (isDrugFlow) {
        if (!refLigandSpec) {
            submitBtnTooltip = 'Select a reference ligand for DrugFlow generation';
            disabled = true;
        }
    } else if (isMolexar) {
        if (!mxRefSpec) {
            submitBtnTooltip = 'Select a reference ligand for Molexar generation';
            disabled = true;
        } else if (mxTask !== 'de_novo' && !mxStartSmiles.trim() && !mxStartMolfile) {
            submitBtnTooltip = `The "${mxTask}" task requires an input structure`;
            disabled = true;
        }
    } else if (!molDataText || molDataText === '') {
        submitBtnTooltip = 'Add some input compounds for AI compound generation before submission';
        disabled = true;
    }

    useEffect(() => {
        setCompounds(App.Workspace.getLoadedCompounds().filter(
            (compound) => !molDataText.includes(compound.getUnnamedSmiles())
        ));
    }, [molDataText]);

    function renderInputs() {
        if (isDrugFlow) {
            return (
                <DrugFlowInputs
                    loadedCompounds={loadedCompounds}
                    refLigandSpec={refLigandSpec}
                    setRefLigandSpec={setRefLigandSpec}
                    nSamples={nSamples}
                    setNSamples={setNSamples}
                    advanced={advanced}
                    setAdvanced={setAdvanced}
                    showAdvanced={showAdvanced}
                    setShowAdvanced={setShowAdvanced}
                />
            );
        }
        if (isMolexar) {
            return (
                <MolexarInputs
                    loadedCompounds={loadedCompounds}
                    refSpec={mxRefSpec}
                    setRefSpec={setMxRefSpec}
                    numSamples={mxNumSamples}
                    setNumSamples={setMxNumSamples}
                    task={mxTask}
                    setTask={setMxTask}
                    startSmiles={mxStartSmiles}
                    setStartSmiles={setMxStartSmiles}
                    startMolfile={mxStartMolfile}
                    setStartMolfile={setMxStartMolfile}
                    advanced={mxAdvanced}
                    setAdvanced={setMxAdvanced}
                    showAdvanced={mxShowAdvanced}
                    setShowAdvanced={setMxShowAdvanced}
                />
            );
        }
        return (
            <>
                {chosenProgram === JobTypes.RXN_ENUM && (
                    <>
                    <Box sx={{ width: '50%' }}>
                        <Autocomplete
                            multiple
                            options={RXN_ENUM_REACTIONS}
                            value={reactionNames}
                            onChange={(_event, newValue) => setReactionNames(newValue)}
                            autoHighlight
                            disableCloseOnSelect
                            filterSelectedOptions
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Reaction(s)"
                                    placeholder={reactionNames.length ? '' : 'All reactions (type to search)'}
                                    helperText="Choose a named reaction(s), or leave blank to enumerate over all reactions."
                                />
                            )}
                        />
                        <ReactionSchemePreview
                            reactions={chosenProgram === JobTypes.RXN_ENUM ? reactionNames : []}
                        />
                        <Box sx={{ marginTop: '0.5rem' }}>
                            <LinkLikeButton onClick={() => setShowDrawReaction(true)}>
                                + Draw a custom reaction
                            </LinkLikeButton>
                            {customRxnfiles.map((r) => (
                                <Box
                                    key={r.id}
                                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                                >
                                    <span>{r.name}</span>
                                    <LinkLikeButton
                                        onClick={() => setCustomRxnfiles(
                                            (prev) => prev.filter((x) => x.id !== r.id)
                                        )}
                                    >
                                        remove
                                    </LinkLikeButton>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    {showDrawReaction && (
                        <ReactionSketcher
                            onAccept={(rxn, drawnName) => setCustomRxnfiles(
                                (prev) => [
                                    ...prev,
                                    {
                                        id: `${Date.now()}-${prev.length}`,
                                        name: drawnName || `Drawn reaction ${prev.length + 1}`,
                                        rxn,
                                    },
                                ]
                            )}
                            onClose={() => setShowDrawReaction(false)}
                        />
                    )}
                    </>
                )}
                {compounds.length > 0 && (
                    <Select
                        label="Add Workspace Compounds"
                        slotProps={{ select: { native: false } }}
                        sx={{ width: '50%', margin: '1rem 0' }}
                    >
                        {compounds.map((compound) => (
                            <MenuItem
                                key={compound.smiles}
                                onClick={() => {
                                    handleCompoundSelect(compound);
                                }}
                            >
                                {compound.displayName}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <CompoundInputBox
                    value={molDataText}
                    setValues={(e) => setMolDataText(e)}
                />
            </>
        );
    }

    return (
        <div>
            <DialogContentSection title="AI Compound Generation" disablePaddingBottom>
                Derive new compounds using an AI program.
            </DialogContentSection>
            <DialogContentSection
                title="1. Specify Compound Input"
                disablePaddingTop
            >
                <Select
                    label="Compound Generation Program"
                    slotProps={{ select: { native: false } }}
                    value={chosenProgram}
                    onChange={(e) => {
                        setChosenProgram(e.target.value);
                    }}
                    sx={{ width: '50%' }}
                >
                    {moleculeGenerationPrograms.map((program) => (
                        <MenuItem key={program} value={program}>
                            {lookupJobDefinition(program)?.getJobTypeName()}
                        </MenuItem>
                    ))}
                </Select>
                {renderInputs()}
                <Box sx={{ width: '50%' }}>
                    <BasicTextField
                        labelText="Job Name (optional)"
                        value={jobName}
                        title="Job Name (optional)"
                        required={false}
                        onChange={(evt) => setJobName(evt.target.value)}
                    />
                </Box>
            </DialogContentSection>
            <DialogContentSection
                title="2. Submit Compound Generation Job"
                disablePaddingBottom
            >
                <Box>
                    <BMapsTooltip title={submitBtnTooltip}>
                        {wrapForDisabledTooltip(
                            <DialogPrimaryButton
                                onClick={handleSubmitClick}
                                style={{ width: '100%' }}
                                disabled={disabled}
                            >
                                Submit for Compound Generation
                            </DialogPrimaryButton>
                        )}
                    </BMapsTooltip>
                    <span style={{ fontStyle: 'italic' }}>
                        Completed jobs will be will be available in the
                        {' '}
                        <LinkLikeButton
                            onClick={() => UserActions.OpenImport('jobs_compounds_tab')}
                            style={{ padding: 0, verticalAlign: 'unset' }}
                        >
                            Compound Jobs
                        </LinkLikeButton>
                        {' '}
                        tab.
                        <br />
                        Load a completed job to view the generated compounds
                        and import them into your workspace.
                    </span>
                </Box>
            </DialogContentSection>
            {!!errors && errors.length > 0 && <ImportErrors errors={errors} /> }
        </div>
    );
}

function DrugFlowInputs({
    loadedCompounds, refLigandSpec, setRefLigandSpec, nSamples, setNSamples,
    advanced, setAdvanced, showAdvanced, setShowAdvanced,
}) {
    const setAdv = (key) => (evt) => setAdvanced({ ...advanced, [key]: evt.target.value });
    return (
        <>
            <Box sx={{ marginTop: '1rem' }}>
                <PlaceHolder $show={loadedCompounds.length === 0}>
                    <>
                        DrugFlow generates molecules into the pocket of the
                        <br />
                        loaded protein. Load a protein with a bound ligand first.
                    </>
                </PlaceHolder>
                {loadedCompounds.length > 0 && (
                    <Select
                        label="Reference Ligand (defines pocket)"
                        slotProps={{ select: { native: false } }}
                        value={refLigandSpec}
                        onChange={(e) => setRefLigandSpec(e.target.value)}
                        sx={{ width: '50%' }}
                    >
                        {loadedCompounds.map((compound) => (
                            <MenuItem key={compound.resSpec} value={compound.resSpec}>
                                {compound.displayName}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>
            <Box sx={{ width: '50%' }}>
                <BasicTextField
                    labelText="Number of molecules"
                    value={nSamples}
                    title="Number of molecules"
                    type="number"
                    required={false}
                    onChange={(evt) => setNSamples(evt.target.value)}
                />
            </Box>
            <Box sx={{ marginTop: '0.75rem' }}>
                <LinkLikeButton
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{ padding: 0 }}
                >
                    {showAdvanced ? 'Hide advanced parameters' : 'Show advanced parameters'}
                </LinkLikeButton>
            </Box>
            {showAdvanced && (
                <Box sx={{ width: '50%' }}>
                    <BasicTextField
                        labelText="Denoising steps (blank = model default)"
                        value={advanced.n_steps || ''}
                        title="Number of denoising steps; higher can improve quality but is slower"
                        type="number"
                        required={false}
                        onChange={setAdv('n_steps')}
                    />
                    <BasicTextField
                        labelText="Batch size (blank = auto)"
                        value={advanced.batch_size || ''}
                        title="Generation batch size; lower uses less GPU memory"
                        type="number"
                        required={false}
                        onChange={setAdv('batch_size')}
                    />
                    <BasicTextField
                        labelText="Molecule size (int or 'min,max', blank = sampled)"
                        value={advanced.molecule_size || ''}
                        title="Fixed number of atoms, or a range like 15,20; blank lets DrugFlow sample the size"
                        required={false}
                        onChange={setAdv('molecule_size')}
                    />
                    <BasicTextField
                        labelText="Pocket distance cutoff (Å, blank = 8.0)"
                        value={advanced.pocket_distance_cutoff || ''}
                        title="Distance around the reference ligand used to define the pocket"
                        type="number"
                        required={false}
                        onChange={setAdv('pocket_distance_cutoff')}
                    />
                    <BasicTextField
                        labelText="Random seed (blank = 42)"
                        value={advanced.seed || ''}
                        title="Random seed for reproducible generation"
                        type="number"
                        required={false}
                        onChange={setAdv('seed')}
                    />
                </Box>
            )}
        </>
    );
}

function MolexarInputs({
    loadedCompounds, refSpec, setRefSpec, numSamples, setNumSamples,
    task, setTask, startSmiles, setStartSmiles,
    startMolfile, setStartMolfile,
    advanced, setAdvanced, showAdvanced, setShowAdvanced,
}) {
    const setAdv = (key) => (evt) => setAdvanced({ ...advanced, [key]: evt.target.value });
    const needsStartSmiles = task !== 'de_novo';
    const currentTask = MOLEXAR_TASKS.find((t) => t.value === task);
    const [showDraw, setShowDraw] = useState(false);
    return (
        <>
            <Box sx={{ marginTop: '1rem' }}>
                <PlaceHolder $show={loadedCompounds.length === 0}>
                    <>
                        Molexar generates molecules into the pocket of the
                        <br />
                        loaded protein. Load a protein with a bound ligand first.
                    </>
                </PlaceHolder>
                {loadedCompounds.length > 0 && (
                    <Select
                        label="Reference Ligand (defines pocket)"
                        slotProps={{ select: { native: false } }}
                        value={refSpec}
                        onChange={(e) => setRefSpec(e.target.value)}
                        sx={{ width: '50%' }}
                    >
                        {loadedCompounds.map((compound) => (
                            <MenuItem key={compound.resSpec} value={compound.resSpec}>
                                {compound.displayName}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>
            <Box sx={{ width: '50%', marginTop: '1rem' }}>
                <Select
                    label="Generation task"
                    slotProps={{ select: { native: false } }}
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    sx={{ width: '100%' }}
                >
                    {MOLEXAR_TASKS.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                            {t.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
            {needsStartSmiles && (
                <Box sx={{ width: '50%', marginTop: '1rem' }}>
                    <BasicTextField
                        labelText="Input SMILES (scaffold / fragment)"
                        value={startSmiles}
                        title="SMILES used as the starting point for the selected task"
                        required
                        onChange={(evt) => setStartSmiles(evt.target.value)}
                    />
                    <Box sx={{ marginTop: '0.25rem' }}>
                        <LinkLikeButton
                            onClick={() => setShowDraw(true)}
                            style={{ padding: 0 }}
                        >
                            Draw structure
                        </LinkLikeButton>
                        {startMolfile && (
                            <span style={{ marginLeft: '0.75rem' }}>
                                <span>structure drawn — SMILES field ignored </span>
                                <LinkLikeButton
                                    onClick={() => setStartMolfile('')}
                                    style={{ padding: 0 }}
                                >
                                    clear
                                </LinkLikeButton>
                            </span>
                        )}
                    </Box>
                    {currentTask?.hint && (
                        <div style={{ fontStyle: 'italic', fontSize: '0.85em', marginTop: '0.25rem' }}>
                            {currentTask.hint}
                        </div>
                    )}
                    {currentTask?.example && (
                        <div style={{ fontSize: '0.85em', marginTop: '0.25rem' }}>
                            <span>Example: </span>
                            <code style={{ wordBreak: 'break-all' }}>
                                {currentTask.example}
                            </code>
                            <br />
                            <LinkLikeButton
                                onClick={() => setStartSmiles(currentTask.example)}
                                style={{ padding: 0 }}
                            >
                                Use this example
                            </LinkLikeButton>
                        </div>
                    )}
                </Box>
            )}
            {needsStartSmiles && showDraw && (
                <MoleculeSketcher
                    onAccept={setStartMolfile}
                    onClose={() => setShowDraw(false)}
                />
            )}
            <Box sx={{ width: '50%' }}>
                <BasicTextField
                    labelText="Number of molecules"
                    value={numSamples}
                    title="Number of molecules"
                    type="number"
                    required={false}
                    onChange={(evt) => setNumSamples(evt.target.value)}
                />
            </Box>
            <Box sx={{ width: '50%', marginTop: '0.75rem' }}>
                <div style={{ fontStyle: 'italic', marginBottom: '0.25rem' }}>
                    Property conditioning (optional)
                </div>
                {MOLEXAR_PROPERTIES.filter((p) => p.primary).map((p) => (
                    <BasicTextField
                        key={p.key}
                        labelText={p.label}
                        value={advanced[p.key] || ''}
                        title={p.title}
                        type="number"
                        required={false}
                        onChange={setAdv(p.key)}
                    />
                ))}
            </Box>
            <Box sx={{ marginTop: '0.75rem' }}>
                <LinkLikeButton
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{ padding: 0 }}
                >
                    {showAdvanced ? 'Hide advanced parameters' : 'Show advanced parameters'}
                </LinkLikeButton>
            </Box>
            {showAdvanced && (
                <Box sx={{ width: '50%' }}>
                    <BasicTextField
                        labelText="Pocket radius (Å, blank = 10)"
                        value={advanced.pocket_radius || ''}
                        title="Distance from any reference-ligand atom used to crop the pocket"
                        type="number"
                        required={false}
                        onChange={setAdv('pocket_radius')}
                    />
                    <BasicTextField
                        labelText="Max pocket atoms (blank = 425)"
                        value={advanced.max_atoms || ''}
                        title="Maximum number of pocket atoms passed to the model"
                        type="number"
                        required={false}
                        onChange={setAdv('max_atoms')}
                    />
                    <BasicTextField
                        labelText="Temperature (blank = 0.8)"
                        value={advanced.temperature || ''}
                        title="Sampling temperature; higher = more diverse"
                        type="number"
                        required={false}
                        onChange={setAdv('temperature')}
                    />
                    <BasicTextField
                        labelText="top_p (blank = 0.95)"
                        value={advanced.top_p || ''}
                        title="Nucleus sampling probability"
                        type="number"
                        required={false}
                        onChange={setAdv('top_p')}
                    />
                    <BasicTextField
                        labelText="top_k (blank = 50)"
                        value={advanced.top_k || ''}
                        title="Top-k sampling cutoff"
                        type="number"
                        required={false}
                        onChange={setAdv('top_k')}
                    />
                    <div style={{ fontStyle: 'italic', margin: '0.5rem 0 0.25rem' }}>
                        Additional property conditioning (blank = off)
                    </div>
                    {MOLEXAR_PROPERTIES.filter((p) => !p.primary).map((p) => (
                        <BasicTextField
                            key={p.key}
                            labelText={p.label}
                            value={advanced[p.key] || ''}
                            title={p.title}
                            type="number"
                            required={false}
                            onChange={setAdv(p.key)}
                        />
                    ))}
                </Box>
            )}
        </>
    );
}

function CompoundInputBox({
    value, setValues,
}) {
    return (
        <Box sx={{ marginTop: '1rem' }}>
            <PlaceHolder $show={value === ''}>
                <>
                    Enter ligand smiles, one per line,
                    <br />
                    or select a compound from the workspace.
                </>
            </PlaceHolder>
            <TextArea
                autoFocus
                multiline
                minRows={1}
                value={value}
                onChange={(e) => setValues(e.target.value)}
                sx={{
                    '& textarea': {
                        resize: 'vertical',
                        minHeight: '6rem',
                    },
                }}
            />
        </Box>
    );
}
